import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/layout/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { PROJECT_STATUS } from "@/lib/discovery";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, Clock, ClipboardCheck, CheckCircle2, Plus, ArrowUpRight } from "lucide-react";

const CARDS = [
  { key: "active", label: "Active Discoveries", icon: Activity, tone: "text-sky-600 bg-sky-50" },
  { key: "awaiting_customer", label: "Awaiting Customer", icon: Clock, tone: "text-amber-600 bg-amber-50" },
  { key: "awaiting_review", label: "Awaiting Review", icon: ClipboardCheck, tone: "text-violet-600 bg-violet-50" },
  { key: "completed", label: "Completed", icon: CheckCircle2, tone: "text-emerald-600 bg-emerald-50" },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  const canCreate = user?.role === "admin" || user?.role === "consultant";

  return (
    <>
      <PageHeader
        title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle="Guided discovery for faster Salesforce implementations."
        actions={canCreate && (
          <Button data-testid="new-project-btn" onClick={() => navigate("/projects/new")} className="rounded-lg bg-sky-600 hover:bg-sky-700">
            <Plus className="mr-1.5 h-4 w-4" /> New Discovery
          </Button>
        )}
      />
      <div className="flex-1 px-6 py-8 lg:px-10">
        {/* Stat cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map((c) => (
            <div key={c.key} data-testid={`stat-${c.key}`}
              className="rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
              <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${c.tone}`}>
                <c.icon className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <div className="font-heading text-4xl font-extrabold tracking-tight text-slate-900">
                {data ? data.stats[c.key] : <Skeleton className="h-9 w-12" />}
              </div>
              <div className="mt-1 text-sm font-medium text-slate-500">{c.label}</div>
            </div>
          ))}
        </div>

        {/* Recent projects */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Recent Discovery Projects</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/projects")} className="text-sky-600 hover:text-sky-700">
              View all <ArrowUpRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <th className="px-6 py-3">Customer</th>
                  <th className="px-6 py-3">Project</th>
                  <th className="px-6 py-3">Product</th>
                  <th className="px-6 py-3 w-48">Progress</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Consultant</th>
                  <th className="px-6 py-3">Activity</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {!data && [1, 2, 3].map((i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="px-6 py-4" colSpan={8}><Skeleton className="h-6 w-full" /></td></tr>
                ))}
                {data?.projects.length === 0 && (
                  <tr><td colSpan={8} className="px-6 py-16 text-center">
                    <p className="font-heading text-lg font-bold text-slate-700">No discovery projects yet</p>
                    <p className="mt-1 text-sm text-slate-400">Create your first guided discovery to get started.</p>
                    {canCreate && <Button onClick={() => navigate("/projects/new")} className="mt-4 rounded-lg bg-sky-600 hover:bg-sky-700"><Plus className="mr-1.5 h-4 w-4" /> New Discovery</Button>}
                  </td></tr>
                )}
                {data?.projects.map((p) => {
                  const st = PROJECT_STATUS[p.status] || { label: p.status, tone: "slate" };
                  return (
                    <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-t border-slate-100 transition-colors hover:bg-slate-50/70">
                      <td className="px-6 py-4 font-semibold text-slate-800">{p.customer_name}</td>
                      <td className="px-6 py-4 text-slate-600">{p.project_name}</td>
                      <td className="px-6 py-4"><span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">{p.product}</span></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Progress value={p.progress} className="h-2 w-24" />
                          <span className="text-xs font-bold text-slate-600">{p.progress}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4"><StatusBadge label={st.label} tone={st.tone} /></td>
                      <td className="px-6 py-4 text-slate-600">{p.consultant_name || "—"}</td>
                      <td className="px-6 py-4 text-slate-400">{timeAgo(p.last_activity)}</td>
                      <td className="px-6 py-4 text-right">
                        <Button size="sm" variant="outline" data-testid={`open-project-${p.id}`} onClick={() => navigate(`/projects/${p.id}`)} className="rounded-lg">Open</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
